/**
 * VIP升级首次下载状态
 */
interface VipUpgradeDownloadStatus {
  /** 是否已完成VIP升级后的首次下载提示 */
  seen: boolean;
  /** 上一次检测时的会员状态（用于检测升级） */
  lastMemberStatus: boolean | null;
  /** 状态是否已从服务端加载 */
  isLoaded: boolean;
}

import { toRefs } from "vue";
import { syncArtworkChannel } from "./channel";
import { defineStore } from "pinia";
import { type LoginResponse, logout } from "@/components/GlobleModal/LoginModal/service";
import { queryRights } from "@/views/power/service";
import { usePayStore } from "../pay";
import { userPackage, vipPackageEnum, vipPackage, packageTimeEnum, getPackageInfo } from "@/assets/pay";
import { message } from "ant-design-vue";
import { useArtworkStore } from "@/store/artwork";
import type { UserPackage } from "@/types/power";
import dayjs, { Dayjs } from "dayjs";
import router from "@/router";
import { sensors } from "@/assets/sensors";
import { getUserCustomStorage, setUserCustomStorage } from "@/views/user/service";
import { useTeamEditionStore } from "@/store/teamEdition";
import { aegis } from "@/utils/monitoring";
import XMSDKWebJs from "@xmiles/sdk-web-js";
import { PRD_ID, CURRENT_CHANNEL, APP_VERSION, APP_VERSION_CODE, IS_TEST, IS_DEBUG } from "@/config/XMSdkConfig";
import { usePackageStore } from "../package";
import { AB_TEST_TYPES } from "@/constants";
import { get } from "lodash";
import { getConsumePower, getConsumePowerLabel } from "./utils";
import { MemberCycle, MemberType } from "./memberButton";
import { GAME_UI_SPLIT_POOL_COUNT_KEYS, isGameUiSplitQuotaKind, pickGameUiSplitQuotaPool, type GameUiSplitQuotaKind } from "./gameUiSplitQuota";
import { meetsMinTier, membershipTierRank, findHighestTierItem, resolveHighestPackageKey, type MemberPackageKey } from "@/utils/memberTier";
import { STORAGE_KEYS } from "@/constants/storage";
import { checkTeamQuotaWarning } from "@/utils/teamQuotaWarning";
// import { useNewUserGuide } from '../newUserGuide';
import { useCreationTask } from "@/hooks/useCreationTask";
import { clearTokenCookie, getCookieByName, getCookieConfig, getURLSearchParams, setTokenCookie } from "@/utils";
import { WatermarkStatusEnum } from "@/types/alipayRealName";
import useLoginStore from "../login";
import { queryUserInfo, updateWatermarkStatus } from "@/api/user";
import { useTempRecordStore } from "@/store/tempRecord";
import useGroupStore from "../group";
import { getActivityRights } from "@/api/rights";
import { globalStore } from "@xmiles/holopix-config";
import { broadcastAuthReset } from "@/utils/authResetChannel";
import { clearBoardLocalData } from "@/utils/boardLocalDataCleanup";
import { ModuleId } from "@xmiles/holopix-types";
import type { IconLabelType } from "@/types/iconLabel";
import { logToken, TokenExpiryReason } from "@/utils/tokenManager";
import {
  hasCompletedSharedAuthMigration,
  markSharedAuthMigrationCompleted,
  resolveSharedAuthCredential,
} from "@/utils/sharedAuthCredential";

/** 并发去重：多入口同时调用 fetchFeatureUpgradeReadStatus 时合并为单次请求，避免竞态导致 isLoaded 已置位但 data 未合并 */
let featureUpgradeReadFetchInFlight: Promise<void> | null = null;
let sharedAuthSyncPromise: Promise<boolean> | null = null;
let sharedAuthInitialized = false;

type UserInfo = LoginResponse;

const initUserInfo: Readonly<UserInfo> = {
  account: "",
  avatar: "",
  id: "",
  nickname: "",
  firstLogin: false,
  isInner: false, // 是否内部用户
  source: "", // 来源
  token: "",
  uniqueId: "",
  paid: false,
  hasPassword: false,
  isWechat: false, // 是否绑定微信
  email: "", // 是否绑定邮箱
  abtest: "", // 订阅a、b用户
  inviteCode: "",
  teamMemberAbtest: false, // 年费会员，一年送2个月的专属abtest
  curTeam: false,
  isAlipaySign: false,
  /** Klein 工作流 V2；未登录或未下发时默认 false */
  goWorkFlowV2: false,
  /** 是否为 QPS 白名单用户（享受增强并发数）；未登录或未下发时默认 false */
  qpsEnabled: false
};

const initSources = {
  dayPower: 0, //每日算力
  dailyPower: 0, // 会员算力
  foreverPower: 0, // 通用算力
  totalSpace: 0, // 已使用空间（MB）
  userMaxSpace: 50 * 1024, // 用户存储上限（MB）
  inviteMembers: 0, // 邀请人数
  invitePoints: 0, // 邀请获得的通用算力
  /**
   * 1. 有memberList代表当前是会员
      2. 没有的话，判断expireMember，如果是true，代表已过期，false就代表没开过会员
   */
  expireMember: false, // 是否有过期会员记录
  lastExpireMemberList: [] as UserPackage[], // 用户过期套餐
  firstMember: false, // 是否为首购用户
  periodType: undefined as 1 | 2 | undefined, // 类型:1正式版 2试用版
  lastExpireMemberTeamList: [] as UserPackage[], // 团队过期套餐
  ctime: 0, // 用户创建时间
  inSell: 0, // （国庆购买记录 0否 1是）
  signStatus: "UNSIGN",
  activityPro: false, // 活动购买月度pro记录 true已购买 false未购买
  activityPlus: false, // 活动购买月度plus记录 true已购买 false未购买
  activityUpgradePlusMonth: false, // 活动购买升级月度plus记录 true已购买 false未购买
  activityUpgradeUltraMonth: false, // 活动购买升级月度Ultra记录 true已购买 false未购买
  activityUpgradePlusYear: false, // 活动购买升级年度Plus记录 true已购买 false未购买
  activityUpgradeUltraYear: false, // 活动购买升级年度Ultra记录 true已购买 false未购买

  freeRenderCount: 0, // 万能渲染免费次数
  freePerspectiveCount: 0, // 自由视角免费次数（AI 创作 module 33）
  qwenFreePerspectiveCount: 0, // Qwen 自由视角免费次数（module 60/61 共享）
  gameUiSplitTrialCount: 0, // 全能拆分未付费免费体验剩余次数（上限 10，永久有效）
  gameUiSplitDailyCount: 0, // 全能拆分会员每日限免剩余次数（上限 10，次日重置）
  gameUiSplitTotalCount: 0, // 全能拆分会员累计限免剩余次数（上限 10，永久）
  gameUiSplitBetaEnabled: false, // 全能拆分 Beta 限免开关（false 时会员直接恢复计价）
  memberTotalPoints: 0, // 个人总算力
  memberSendPoints: 0, // 个人周期发放算力
  teamTotalPoints: 0, // 团队总算力
  teamSendPoints: 0, // 团队周期发放算力
  teamSubPoints: 0, // 团队已使用算力
  teamQuotaLimit: null as number | null, // 团队成员算力上限（null 表示无限制）
  isSaaS: null as boolean | null, // 是否为 SaaS 商品（支付宝 AI 订阅场景使用）
  /**
   * SVIP 首期低价商品购买记录：true 已买（不可再买 158 元首期低价），false 未买
   * 对应 getRights.svipFirstPurchaseLowPrice：0 否 / 1 是
   */
  svipFirstPurchaseLowPrice: false,
  /** getRights.trialTier：0 未买过；80/70/60 为已购最高试用档 */
  trialTier: 0,
  /** getRights.currentActiveTrial：当前有效会员是否由 7 天低价试用产生（正在试用中） */
  currentActiveTrial: false
};

type ActivityRightKind =
  | "render"
  | "freePerspective"
  | "qwenFreePerspective"
  // 全能拆分限免按池记账（trial/total/daily，映射与判定见 ./gameUiSplitQuota）
  | GameUiSplitQuotaKind;
type PendingActivityRightDeduction = {
  kind: ActivityRightKind;
  deductedAt: number;
};

export const useUserStore = defineStore("user", {
  state: () => ({
    userInfo: { ...initUserInfo },
    sources: { ...initSources },
    /** 仅内存中递增；较早的权益响应不得覆盖购买后发起的新查询。 */
    rightsRequestId: 0,
    packages: [] as UserPackage[], // 用户开通套餐
    memberPointsDdl: "", // 续费后，下次会员算力生效时间
    teamPointsDdl: "", // 团队版续费后，下次会员算力生效时间
    isShowNewGuide: true,
    inviteId: "",
    recommendModelId: "",
    curTeam: false,
    newUserInfoCollectModalOpen: false, // 新用户收集弹窗
    newUserInfoCollectForBModalOpen: false, // 新用户收集弹窗 B版本
    hasFirstOrder: false, // 是否拥有首购资格
    hasFirstSign: false,
    hasFreeTrain: false, // 是否有免费训练的权益
    hasQiwei: false, // 是否加入企业微信
    planB: {
      isEnabled: !true // 是否开启planB
    },
    enterpriseWeChatModalHasClose: false, // 企业微信引流弹窗是否关闭
    testAlipaySign: true, // 测试支付宝自动扣费
    /**
     * 暂存用户是否为过期用户，避免购买成功后，数据更新导致判断异常
     * 会员过期再续费是算力立即发放
     * */
    isExpireUserBeforePay: false,
    isFromOfficialWebsite: false, // 是否从品牌宣传页面登录
    deviceEnv: "", // 设备环境 pc | h5
    isProcessingLoginAfterHandlers: false, // 是否正在处理登录后逻辑，防止重复触发
    hasCouponFlowCompleted: false, // 是否已完成优惠券流程（显示过或用户主动关闭）
    isShowModelTermsNoticeModal: false, // 是否显示模型条款注意弹窗
    activityRightsLastSyncedAt: 0,
    pendingActivityRightDeductions: new Map<string, PendingActivityRightDeduction>(),
    /** 功能升级已读状态缓存 */
    featureUpgradeRead: {
      data: {} as Record<string, boolean>,
      isLoaded: false
    },
    /** 用户设置弹窗状态（全局控制，用于从其他组件打开设置页） */
    userSettingsModal: {
      open: false,
      defaultKey: undefined as string | undefined
    },
    /**
     * 水印弹窗首次观看状态
     * - export: 导出场景是否看过弹窗（控制弹窗显示和Bar显示）
     * - settings: 设置页场景是否看过弹窗
     * - isLoaded: 是否已从服务端加载
     */
    watermarkFirstSeen: {
      export: false,
      settings: false,
      isLoaded: false
    },
    /**
     * VIP升级首次下载状态
     * - seen: 是否已完成VIP升级后的首次下载提示
     * - lastMemberStatus: 上一次检测时的会员状态（用于检测升级）
     * - isLoaded: 是否已从服务端加载
     */
    vipUpgradeDownloadStatus: {
      seen: false,
      lastMemberStatus: null,
      isLoaded: false
    } as VipUpgradeDownloadStatus
  }),
  getters: {
    /**
     * 水印/认证状态枚举值 (0-5)
     * 直接从 userInfo.realizeResult 读取，替代旧的 certFyResult + watermarkState 方案
     */
    watermarkStatus(): WatermarkStatusEnum {
      return (this.userInfo.realizeResult ?? WatermarkStatusEnum.WAITING) as WatermarkStatusEnum;
    },

    /** 是否可以操作 Toggle 开关（已签署状态 + 是会员） */
    canToggleWatermark(): boolean {
      const rr = this.userInfo.realizeResult ?? 0;
      return rr >= WatermarkStatusEnum.SIGNED_WATERMARK_ON && this.isMemberUser;
    },

    /** 是否已去除水印（状态5 = 已关闭水印） */
    isWatermarkRemoved(): boolean {
      return this.userInfo.realizeResult === WatermarkStatusEnum.SIGNED_WATERMARK_OFF;
    },
    /**
     * 是否开启支付宝自动扣费
     * @returns 是否开启支付宝自动扣费
     */
    isAlipaySignUser({ userInfo }): boolean {
      return userInfo.isAlipaySign && this.testAlipaySign;
    },
    /**
     * B 组 SVIP 连续包月是否可购买首期低价（158 元）
     * getRights.svipFirstPurchaseLowPrice：0 未买可买 / 1 已买走续费 174 元
     */
    canBuySvipFirstPurchaseLowPrice({ sources }): boolean {
      return !sources.svipFirstPurchaseLowPrice;
    },
    // 是否任务队列2.0类型用户
    isTaskQueue2User({ userInfo }): boolean {
      return userInfo.qtest === "q1";
    },
    /**
     * 用户AB测试,是否需要截取时间,传A 以及时间 判断是不是还是A
     * @param state store数据源
     * @returns type: 'a' | 'b' time 时间
     */
    isABtestUser(state) {
      return (type: "a" | "b", time?: string | Dayjs) => {
        const { abtest, ctime } = state.userInfo;
        // 如果注册时间小于开始时间，直接返回 a
        if (!!time && dayjs(ctime || 0).isBefore(dayjs(time))) {
          return AB_TEST_TYPES.A == type;
        }
        const defaultAB = abtest || AB_TEST_TYPES.A;
        return defaultAB == type;
      };
    },
    /** 获取当前用户会员类型和周期 */
    currentMemberInfo({ packages }): { type: MemberType; cycle: MemberCycle | null } {
      if (packages.length === 0) {
        return {
          type: MemberType.FREE,
          cycle: null
        };
      }

      // 确定会员类型（优先级：ultra > plus > pro）
      const isMemberUltra = packages.some((item) => item.memberType === MemberType.ULTRA);
      const isMemberPlus = packages.some((item) => item.memberType === MemberType.PLUS);
      const memberType = isMemberUltra ? MemberType.ULTRA : isMemberPlus ? MemberType.PLUS : MemberType.PRO;

      // 确定会员周期（取与最高档位对应的套餐行）
      const memberPackage = findHighestTierItem(packages) || packages[0];
      let memberCycle: MemberCycle;
      if (memberType === MemberType.ULTRA) {
        if (memberPackage.signNo) {
          memberCycle = /season/.test(memberPackage.duration)
            ? MemberCycle.SEASON
            : /year/.test(memberPackage.duration)
              ? MemberCycle.YEAR
              : MemberCycle.CONTINUOUS_MONTH;
        } else if (/season/.test(memberPackage.duration)) {
          memberCycle = MemberCycle.SEASON;
        } else if (/year/.test(memberPackage.duration)) {
          memberCycle = MemberCycle.YEAR;
        } else {
          memberCycle = MemberCycle.MONTH;
        }
      } else if (memberPackage.signNo) {
        // 存在签约号表示是连续订阅
        memberCycle = memberType === MemberType.PLUS ? MemberCycle.HALF_SUBSCRIBE : MemberCycle.SUBSCRIBE;
      } else if (/season/.test(memberPackage.duration)) {
        memberCycle = MemberCycle.SEASON;
      } else if (/year/.test(memberPackage.duration)) {
        memberCycle = MemberCycle.YEAR;
      } else {
        memberCycle = memberType === MemberType.PLUS ? MemberCycle.HALF_SUBSCRIBE : MemberCycle.SUBSCRIBE;
      }

      return {
        type: memberType,
        cycle: memberCycle
      };
    },

    /** 是否开通连续包月标准版会员（规则1） */
    isMonthPro({ packages }) {
      return packages.some(
        (item) => item.memberType === MemberType.PRO && /^month/.test(item.duration) && item.signNo // 存在有效签约号
      );
    },

    /** 是否开通连续包半月专业版会员（规则2） */
    isHalfSubscribePlus({ packages }) {
      return packages.some(
        (item) => item.memberType === MemberType.PLUS && /month/.test(item.duration) && item.signNo // 存在有效签约号
      );
    },

    /** 是否开通按季购买标准版会员（规则3）*/
    isSeasonPro({ packages }) {
      return packages.some(
        (item) =>
          item.memberType === MemberType.PRO &&
          /^season$/.test(item.duration) && // 按季购买
          !item.signNo // 无签约号（非连续订阅）
      );
    },

    /** 是否开通按季购买专业版会员（规则4） */
    isSeasonPlus({ packages }) {
      return packages.some(
        (item) => item.memberType === MemberType.PLUS && /^season$/.test(item.duration) && !item.signNo // 无签约号
      );
    },

    /** 是否开通按年购买标准版会员（规则5） */
    isYearlyPro({ packages }) {
      return packages.some(
        (item) => item.memberType === MemberType.PRO && /^year$/.test(item.duration) && !item.signNo // 非连续订阅
      );
    },

    /** 是否开通按年购买专业版会员（规则6） */
    isYearlyPlus({ packages }) {
      return packages.some(
        (item) => item.memberType === MemberType.PLUS && /^year$/.test(item.duration) && !item.signNo // 非连续订阅
      );
    },
    /** 是否为首购用户 */
    isFirstPurchase({ sources }) {
      return sources.firstMember;
    },

    /**
     * 从未买过会员的用户
     */
    isCleverUser(): boolean {
      return this.highestPackage === "free" && !this.memberExpireTime;
    },

    /**用户当前是否已登录 */
    isLogin({ userInfo }): boolean {
      return !!userInfo.token;
    },
    isEnterprise({ userInfo }): boolean {
      // 是否为企业用户
      return !!userInfo.isInner;
    },
    freeSpace({ sources }): number {
      // 剩余存储空间（MB）
      const { totalSpace, userMaxSpace } = sources;
      return userMaxSpace - totalSpace;
    },
    spaceRunOut(): boolean {
      // 存储空间即将耗尽
      return this.freeSpace > 1 && this.freeSpace <= 0.5 * 1024; // 剩余可用空间不足 0.5 GB
    },
    spaceFilled(): boolean {
      // 存储空间已满
      return this.freeSpace <= 1; // 剩余可用空间不超过 1 MB
    },
    powerNumber({ sources }): number {
      // 可用算力
      return sources.dailyPower + sources.foreverPower + sources.dayPower;
    },
    /** 用户当前最高套餐（支持同时开通vip、svip） */
    highestPackage({ packages }): keyof typeof userPackage {
      return resolveHighestPackageKey(packages);
    },
    /** 当前套餐档位权重（free=0 … ultra 最高） */
    membershipTierRank(): number {
      return membershipTierRank(this.highestPackage);
    },
    /** 当前套餐是否不低于指定档位（含相等） */
    hasMembershipAtLeast() {
      return (min: MemberPackageKey) => meetsMinTier(this.highestPackage, min);
    },
    defaultMemberTimeMode({ packages }): keyof typeof packageTimeEnum | undefined {
      // 打开会员开通弹窗选中的默认会员时长（与 currentMemberInfo 一致：取最高档对应套餐行）
      if (packages.length === 0) {
        return undefined;
      }
      const tierRow = findHighestTierItem(packages) || packages[0];
      const d = tierRow.duration;
      if (this.highestPackage === "ultra") {
        if (d === "month" || /^month/.test(d)) {
          return "subscribe";
        }
        return d as keyof typeof packageTimeEnum;
      }
      if (["week", "month"].includes(d)) {
        return this.highestPackage === "vip" ? "subscribe" : "halfSubscribe";
      }
      return d as keyof typeof packageTimeEnum;
    },
    packageKeys({ packages }): (keyof typeof vipPackage)[] {
      return packages.map((item) => {
        return vipPackageEnum[item.memberType as keyof typeof vipPackageEnum];
      }) as any;
    },
    vipEndTime({ packages }): string {
      // 会员到期时间
      let entTime = dayjs().format("YYYY/MM/DD");

      packages.map((item) => {
        if (dayjs(item.endTime).isAfter(entTime)) {
          entTime = dayjs(item.endTime).format("YYYY/MM/DD");
        }
      });

      return entTime;
    },
    memberRemainingDays(): number {
      // 会员剩余天数
      return dayjs(this.vipEndTime).diff(dayjs(), "day");
    },

    /** 年费 6.9 折续费优惠已下线，临期/过期用户均统一 7 折 */
    isEligibleForRenewalDiscount(): boolean {
      return false;
    },

    isMaiXueUser({ userInfo }): boolean {
      // 迈学用户
      return this.isLogin && userInfo.paid;
    },
    /**
     * 是否显示vip相关内容
     * https://www.teambition.com/task/684939a077ba0a2662a0e1f8 放开限制
     */
    showVip(): boolean {
      // 是否显示vip相关内容
      // return !this.isMaiXueUser;
      return true;
    },
    packageName(): string {
      // 当前套餐名称
      return this.showVip ? userPackage[this.highestPackage].label : "无限版";
    },
    /** 是否订阅会员 */
    isSubscribeUser({ packages }): boolean {
      return packages.length > 0 && packages.some((item) => item.signNo);
    },
    // 是否过期用户
    isExpireUser({ packages, sources }): boolean {
      return packages.length === 0 && sources.expireMember;
    },
    // 团队试用版本是否已过期
    isTeamTrialExpireUser({ sources }): boolean {
      const teamEditionStore = useTeamEditionStore();
      const isTeam = teamEditionStore.userType === "team";
      return isTeam && sources.lastExpireMemberList.length > 0 && sources.periodType === 2;
    },
    /**
     * 会员最近已经过期的,还未过期的是无法在此变量展示
     */
    memberExpireTime({ sources }): string {
      if (sources.lastExpireMemberList[0]) {
        return dayjs(sources.lastExpireMemberList[0]?.endTime).format("YYYY.MM.DD");
      }
      return "";
    },
    /** 是否单独开通会员 */
    isOrderUser(): boolean {
      // return this.highestPackage !== 'free' && !this.isSubscribeUser;
      return this.highestPackage !== "free";
    },
    // 拥有试用会员的用户
    isTryMemberUser(): boolean {
      // return userInfo.abtest === 'b';
      return false;
    },
    // 会员用户
    isMemberUser(): boolean {
      return this.highestPackage !== "free";
    },
    // 当前是团队版登录
    isGroupLogin({ userInfo }): boolean {
      if (this.isLogin) {
        return !!userInfo.curTeam;
      }
      return false;
    },
    // 拥有周会员活动用户
    hasWeekMember(): boolean {
      // return !this.isGroupLogin
      //   && this.hasFirstOrder
      //   && this.hasFirstSign
      //   && this.userInfo.abtest === 'b';
      return false;
    },
    // 周会员是否可用
    weekMemberUsable(): boolean {
      return this.hasWeekMember && !this.isMemberUser;
    },
    // 用户拥有免费训练的权益
    userHasFreeTrain(): boolean {
      return !this.isGroupLogin && this.isMemberUser && (this.hasFreeTrain || false) && this.enterpriseWeChatModalHasClose;
    },
    vipPackageQuestion({ packages }) {
      const formatDate = (time: string) => dayjs(time).format("YYYY/MM/DD HH:mm");
      let vipPackageQuestion = "";
      if (packages.length === 0) {
        vipPackageQuestion = "";
      } else if (packages.length === 1) {
        const vipLabel = getPackageInfo(packages[0].memberType).label;
        const startTime = formatDate(packages[0].startTime);
        const entTime = formatDate(packages[0].endTime);
        vipPackageQuestion = `当前会员等级为${vipLabel}会员：生效至截至日期为：${startTime}-${entTime}`;
      } else {
        const [vipPackage] = packages.filter((item) => item.memberType === MemberType.PRO);
        const [svipPackage] = packages.filter((item) => item.memberType === MemberType.PLUS);

        if (vipPackage && svipPackage) {
          const vipPackageStartTime = formatDate(vipPackage.startTime);
          const vipPackageEndTime = formatDate(vipPackage.endTime);
          const svipPackageStartTime = formatDate(svipPackage.startTime);
          const svipPackageEndTime = formatDate(svipPackage.endTime);

          vipPackageQuestion = `
      当前会员等级为专业版叠加标准版会员：<br>
      1) 专业版生效至截至日期为：${svipPackageStartTime}-${svipPackageEndTime}<br>
      2) 标准版生效至截至日期为：${vipPackageStartTime}-${vipPackageEndTime}
    `;
        } else {
          const pkg = vipPackage || svipPackage || packages[0];
          const vipLabel = getPackageInfo(pkg.memberType).label;
          const startTime = formatDate(pkg.startTime);
          const endTime = formatDate(pkg.endTime);
          vipPackageQuestion = `当前会员等级为${vipLabel}会员：生效至截至日期为：${startTime}-${endTime}`;
        }
      }
      return vipPackageQuestion;
    },

    /**
     * 检查指定功能的升级角标是否已读
     * @param featureKey 功能标识，如 "render"
     * @param version 版本号，如 "1.2"
     * @returns 是否已读
     */
    isFeatureUpgradeRead(): (featureKey: string, version: string) => boolean {
      return (featureKey: string, version: string) => {
        const key = `${featureKey}V${version.replace(/\./g, "")}`;
        return !!this.featureUpgradeRead.data[key];
      };
    },

    /**
     * 获取功能应显示的角标类型
     * 如果 upgrade 已读，返回 null 不显示角标
     *
     * @param config.featureKey 功能标识，如 "render"
     * @param config.configuredLabel 配置的角标类型
     * @param config.version 当前升级版本号，如 "1.2"
     */
    getDisplayLabel(): (config: { featureKey?: string; configuredLabel?: IconLabelType; version?: string }) => IconLabelType | null {
      return (config: { featureKey?: string; configuredLabel?: IconLabelType; version?: string }) => {
        const { featureKey, configuredLabel, version } = config;

        // 如果没有配置角标，不显示
        if (!configuredLabel) return null;

        // 如果不是 upgrade 类型，直接显示
        if (configuredLabel !== "upgrade") return configuredLabel;

        // 如果是 upgrade 类型，检查是否已读
        if (featureKey && version) {
          const key = `${featureKey}V${version.replace(/\./g, "")}`;
          const isRead = !!this.featureUpgradeRead.data[key];
          // 已读则不显示角标
          return isRead ? null : "upgrade";
        }

        // 没有 featureKey 或 version，默认显示 upgrade
        return "upgrade";
      };
    },

    /** 是否应显示水印（未去水印时为 true，即状态 !== 5） */
    shouldShowWatermark(): boolean {
      return !this.isWatermarkRemoved;
    },

    /** 导出后是否应显示 Snackbar 提示（已签署但水印仍开着，且已看过导出弹窗） */
    shouldShowSnackbar(): boolean {
      // 必须已看过导出场景的首次弹窗（避免首次导出就显示Bar，太打扰）
      if (!this.watermarkFirstSeen.export) return false;
      // 已签署但水印开着（状态4）
      return this.userInfo.realizeResult === WatermarkStatusEnum.SIGNED_WATERMARK_ON;
    }
  },
  actions: {
    /**
     * 标记优惠券流程已完成
     */
    markCouponFlowCompleted() {
      this.hasCouponFlowCompleted = true;
    },
    setIsExpireUserBeforePay(isExpireUserBeforePay: boolean) {
      console.log("当前是过期用户", isExpireUserBeforePay);
      this.isExpireUserBeforePay = isExpireUserBeforePay;
    },
    resetSharedAuthUser(token = "") {
      const prevToken = this.userInfo.token;
      globalStore.set("token", token || undefined);
      this.userInfo = { ...initUserInfo, token };
      this.sources = { ...initSources };
      this.packages = [];
      this.curTeam = false;
      // 通知其他标签页同步清理：token 被清空或变更为新值时广播
      if (prevToken && prevToken !== token) {
        broadcastAuthReset();
      }
    },
    async synchronizeSharedAuthSession(): Promise<boolean> {
      if (sharedAuthInitialized) return false;
      if (sharedAuthSyncPromise) return sharedAuthSyncPromise;

      sharedAuthSyncPromise = (async (): Promise<boolean> => {
        let pulledUserInfo = false;
        const { name } = getCookieConfig();
        const cookieToken = getCookieByName(name);
        const persistedToken = this.userInfo.token || "";
        const resolution = resolveSharedAuthCredential({
          cookieToken,
          persistedToken,
          migrationCompleted: hasCompletedSharedAuthMigration(),
        });

        if (resolution.action === "use-cookie") {
          markSharedAuthMigrationCompleted();
          if (resolution.identityChanged) {
            this.resetSharedAuthUser(resolution.token);
            try {
              await this.queryUserInfo();
              pulledUserInfo = true;
            } catch (error) {
              // 共享 Cookie 携带的 token 已失效（90000，如微信/钉钉残留的过期登录态）：
              // 清理无效凭证并降级为游客态继续访问，避免路由守卫中止导航，
              // 导致分享画板等游客可访问页面无法打开。
              const code = Number((error as any)?.code);
              if (code === 90000) {
                logToken("共享 Cookie 校验失败，降级为游客态", {
                  code,
                  tokenPrefix: (this.userInfo.token || "").substring(0, 8),
                });
                this.resetSharedAuthUser();
                await clearTokenCookie();
              } else {
                throw error;
              }
            }
          } else {
            globalStore.set("token", resolution.token);
          }
        } else if (resolution.action === "migrate-persisted") {
          setTokenCookie(resolution.token);
          markSharedAuthMigrationCompleted();
          globalStore.set("token", resolution.token);
        } else if (resolution.action === "clear-persisted") {
          this.resetSharedAuthUser();
        }

        sharedAuthInitialized = true;
        return pulledUserInfo;
      })().finally(() => {
        sharedAuthSyncPromise = null;
      });

      return sharedAuthSyncPromise;
    },
    updateUserInfo(newUserInfo: Partial<UserInfo>) {
      const prevToken = this.userInfo.token;
      const prevUniqueId = this.userInfo.uniqueId;
      const isNewLogin = Boolean(newUserInfo.token) && newUserInfo.token !== prevToken;
      /** 切换账号：token 变化，或已登录用户 uniqueId 变化（不依赖 token 是否出现在本次 patch 里） */
      const shouldResetFeatureUpgradeRead =
        isNewLogin || (Boolean(newUserInfo.uniqueId) && prevUniqueId !== "" && newUserInfo.uniqueId !== prevUniqueId);

      if (newUserInfo.token) {
        globalStore.set("token", newUserInfo.token);
        setTokenCookie(newUserInfo.token);
        markSharedAuthMigrationCompleted();
      }

      this.userInfo = { ...this.userInfo, ...newUserInfo };
      this.userInfo.abtest = this.userInfo.abtest.toLocaleLowerCase();
      this.userInfo.teamMemberAbtest = this.userInfo.teamMemberAbtest ?? false;
      // 新登录时重新初始化团队信息
      const groupStore = useGroupStore();
      groupStore.reinitializeGroup();
      // 如果是新登录，重置优惠券流程状态和登录后处理状态
      if (isNewLogin) {
        this.hasCouponFlowCompleted = false;
        this.isProcessingLoginAfterHandlers = false;
      }
      if (shouldResetFeatureUpgradeRead) {
        // 游客态会清空 data 且 isLoaded=true 导致登录后早退；persist 曾把上一账号已读写入 localStorage 换号后误恢复——均依赖此处重置后重新拉接口
        this.resetFeatureUpgradeReadStatus();
      }

      if (this.userInfo.uniqueId) {
        aegis?.setConfig({
          uin: this.userInfo.uniqueId
        });

        XMSDKWebJs.init({
          prdId: PRD_ID,
          deviceId: this.userInfo.uniqueId,
          currentChannel: CURRENT_CHANNEL,
          appVersion: APP_VERSION,
          appVersionCode: APP_VERSION_CODE,
          isTest: IS_TEST,
          isDebug: IS_DEBUG
        });
      } else {
        aegis?.setConfig({
          uin: ""
        });
      }

      const teamEditionStore = useTeamEditionStore();
      teamEditionStore.userType = this.userInfo.curTeam ? "team" : "personal";
    },
    async queryCertFyResult() {
      // v2 已废弃：certFyResult 不再使用，realizeResult 统一从 userInfo 获取
      // 此方法保留空实现以防有外部引用，后续可完全删除
    },
    async queryUserInfo() {
      if (this.isLogin) {
        const { data } = await queryUserInfo<UserInfo>();
        this.updateUserInfo(data);
        this.queryCertFyResult();
        // 加载水印弹窗首次状态
        await this.loadWatermarkFirstSeen();
      }
    },
    async logout(params?: Partial<Record<"useLogout" | "useModal" | "redirect" | "reason" | "source", boolean | string | TokenExpiryReason>>) {
      const useLogout = get(params, "useLogout", true);
      const useModal = get(params, "useModal", false);
      const redirect = get(params, "redirect", "");
      const loginStore = useLoginStore();
      // 记录登出前是否真的登录过：游客态误触 logout 时不能再 reload，否则会死循环
      const wasLogin = this.isLogin;
      try {
        const { clearAllTasks } = useCreationTask();

        if (wasLogin && useLogout) {
          await logout();
          clearAllTasks();
          sensors.logout(true);
          // 主动登出会清服务端 token 使该账号全部端下线，上报调用入口便于回溯"莫名掉线"来源
          logToken("用户主动登出", {
            source: get(params, "source", "unknown"),
            reason: get(params, "reason", "manual"),
            url: window.location.href,
          });
        }
      } catch (error) {
      } finally {
        if (wasLogin) {
          await clearBoardLocalData();
        }
        const tempRecordStore = useTempRecordStore();
        // 退出登录时清理 token 周期数据
        tempRecordStore.onUserLogout();
        globalStore.set("token", undefined);

        this.packages = [];
        this.updateUserInfo({ ...initUserInfo });
        this.sources = { ...initSources };
        this.curTeam = false;

        const teamEditionStore = useTeamEditionStore();
        teamEditionStore.resetFormData();
        teamEditionStore.resetResponseData();
        teamEditionStore.resetAddSeat();

        const artworkStore = useArtworkStore();
        artworkStore.resetArtwork();

        // 清理团队相关缓存
        const groupStore = useGroupStore();
        groupStore.clearGroupCache();

        localStorage.removeItem("pinoWebMail");
        localStorage.removeItem("pinoMessagePush");
        localStorage.removeItem(STORAGE_KEYS.RANDOM_PROMPT_AUTO_SHOW_KEY);
        this.hasCouponFlowCompleted = false;
        this.isProcessingLoginAfterHandlers = false;
        // 重置水印弹窗状态
        this.resetWatermarkFirstSeen();

        // 清除cookie
        await clearTokenCookie();

        // 通知其他标签页同步清理 auth 状态，避免旧 token 被写回 localStorage
        broadcastAuthReset();

        // 游客态下接口返回 90000 时可能误调 logout；跳过跳转/刷新，避免刷新死循环
        if (!wasLogin) {
          return;
        }

        if (useModal) {
          loginStore.open = true;

          router.replace({
            name: "workbench",
            query: {
              redirect: redirect as string
            }
          });
        } else {
          //退出登录直接跳转到品牌页面
          if (import.meta.env.VITE_ENV != "development") {
            window.location.href = import.meta.env.VITE_SERVICE_DOMAIN;
          } else {
            router.replace({ name: "workbench" });
            location.reload();
          }
        }
      }
    },

    /**
     * 查询用户权益信息
     * 主要功能:
     * 1. 获取用户算力相关数据(日常算力、通用算力等)
     * 2. 获取用户存储空间信息
     * 3. 获取会员过期和邀请相关信息
     * 4. 根据用户套餐类型(svip/vip/free)设置存储空间上限
     * 5. 设置首购优惠折扣状态
     * 6. 可选择是否更新生图通道(加速/普通)
     * @param updateChannel - 是否需要更新生图通道
     */
    async queryUserRights({ updateChannel = false } = {}) {
      const rightsUserId = this.userInfo.id;
      const rightsRequestId = ++this.rightsRequestId;
      const { data } = await queryRights();
      if (this.userInfo.id !== rightsUserId || this.rightsRequestId !== rightsRequestId) return;

      const {
        dailyPower,
        dayPower,
        foreverPower,
        totalSpace,
        expireMember,
        inviteMembers,
        invitePoints,
        lastExpireMemberList,
        firstMember,
        periodType,
        lastExpireMemberTeamList,
        inSell,
        signStatus,
        memberTotalPoints,
        memberSendPoints,
        teamTotalPoints,
        teamSendPoints,
        teamSubPoints,
        teamQuotaLimit,
        activityPro,
        activityPlus,
        activityUpgradePlusMonth,
        activityUpgradeUltraMonth,
        activityUpgradePlusYear,
        activityUpgradeUltraYear,
        isSaaS,
        svipFirstPurchaseLowPrice,
        trialTier,
        currentActiveTrial
      } = toRefs(this.sources);

      dayPower.value = get(data, "dayPoints", 0);
      dailyPower.value = data.totalPoints;
      foreverPower.value = data.foreverPoints;
      totalSpace.value = data.totalSpace;
      expireMember.value = data.expireMember;
      inviteMembers.value = data.inviteMembers;
      invitePoints.value = data.invitePoints;
      lastExpireMemberList.value = data.lastExpireMemberList ?? [];
      firstMember.value = data.firstMember;
      periodType.value = data.periodType;
      lastExpireMemberTeamList.value = data.lastExpireMemberTeamList ?? [];
      inSell.value = data.inSell ? 1 : 0;
      signStatus.value = data.signStatus;
      activityPro.value = !!data.activityPro;
      activityPlus.value = !!data.activityPlus;
      activityUpgradePlusMonth.value = !!data.activityUpgradePlusMonth;
      activityUpgradeUltraMonth.value = !!data.activityUpgradeUltraMonth;
      activityUpgradePlusYear.value = !!data.activityUpgradePlusYear;
      activityUpgradeUltraYear.value = !!data.activityUpgradeUltraYear;

      memberTotalPoints.value = data.memberTotalPoints || 0;
      memberSendPoints.value = data.memberSendPoints || 0;
      teamTotalPoints.value = data.teamTotalPoints || 0;
      teamSendPoints.value = data.teamSendPoints || 0;
      teamSubPoints.value = data.teamSubPoints ?? 0;
      teamQuotaLimit.value = data.teamQuotaLimit ?? null;
      isSaaS.value = data.isSaaS ?? null;
      svipFirstPurchaseLowPrice.value = !!data.svipFirstPurchaseLowPrice;
      trialTier.value = data.trialTier ?? 0;
      currentActiveTrial.value = !!data.currentActiveTrial;

      // 团队模型限制黑名单：从 getRights 写入 userInfo，供 useTeamModelRestriction 读取
      if (data.moduleLimits !== undefined) {
        this.userInfo.moduleLimits = data.moduleLimits;
      }

      this.packages = data.memberList || [];

      // 算力生效日期
      this.memberPointsDdl = data.memberPointsDdl;
      this.teamPointsDdl = data.teamPointsDdl;

      this.hasFreeTrain = !data.freeTrainId;

      // 根据用户最高套餐设置用户存储上限
      const highestKey = resolveHighestPackageKey(this.packages);
      const spaceGB = userPackage[highestKey].space;
      if (this.isGroupLogin) {
        this.sources.userMaxSpace = spaceGB * 1024 * data.headCount;
      } else {
        this.sources.userMaxSpace = spaceGB * 1024;
      }

      // 首购优惠
      const payStore = usePayStore();
      this.hasFirstOrder = data.firstOrder;
      this.hasFirstSign = data.firstSign;
      payStore.hasSubscribeDiscount = data.firstSign;

      // 生图默认通道
      if (updateChannel) {
        syncArtworkChannel(this.packages.length > 0);
      }

      this.hasQiwei = data.hasQiwei;

      // 更新package store中的价格和按钮状态
      const packageStore = usePackageStore();
      packageStore.updatePackagePrices();

      const teamEditionStore = useTeamEditionStore();
      checkTeamQuotaWarning({
        teamSubPoints: teamSubPoints.value,
        teamQuotaLimit: teamQuotaLimit.value,
        isTeamEdition: teamEditionStore.isTeamEdition,
        warn: (content) => message.warning(content)
      });

      return this.sources;
    },

    /**
     * 查询用户活动权益（免费次数等）
     * 用途：获取万能渲染、自由视角等模块的免费次数
     * 调用时机：
     * 1. 进入工作区时
     * 2. 执行生图操作后
     */
    async queryActivityRights() {
      // 登录用户才需要查询
      if (!this.isLogin) {
        return;
      }
      const { freeRenderCount, freePerspectiveCount, qwenFreePerspectiveCount, gameUiSplitTrialCount, gameUiSplitDailyCount, gameUiSplitTotalCount, gameUiSplitBetaEnabled } = toRefs(this.sources);

      try {
        const { data: rightsData } = await getActivityRights(this.userInfo.id);
        freeRenderCount.value = rightsData?.universalRendering || 0;
        freePerspectiveCount.value = rightsData?.freePerspective || 0;
        qwenFreePerspectiveCount.value = rightsData?.qwenFreePerspective ?? 0;
        // 全能拆分限免（TB-9531）：后端按用户身份下发对应池，缺省按 0/关兜底
        gameUiSplitTrialCount.value = rightsData?.gameUiSplitTrial ?? 0;
        gameUiSplitDailyCount.value = rightsData?.gameUiSplitDaily ?? 0;
        gameUiSplitTotalCount.value = rightsData?.gameUiSplitTotal ?? 0;
        gameUiSplitBetaEnabled.value = rightsData?.gameUiSplitBetaEnabled === true;
        this.activityRightsLastSyncedAt = Date.now();
        this.applyPendingActivityRightDeductions();
      } catch (error) {
        console.error("获取活动权益失败:", error);
        freeRenderCount.value = 0;
        freePerspectiveCount.value = 0;
        qwenFreePerspectiveCount.value = 0;
        gameUiSplitTrialCount.value = 0;
        gameUiSplitDailyCount.value = 0;
        gameUiSplitTotalCount.value = 0;
        gameUiSplitBetaEnabled.value = false;
        this.activityRightsLastSyncedAt = Date.now();
        this.applyPendingActivityRightDeductions();
      }
    },

    applyPendingActivityRightDeductions() {
      if (!this.pendingActivityRightDeductions?.size) {
        return;
      }

      for (const { kind } of this.pendingActivityRightDeductions.values()) {
        if (kind === "render") {
          if (this.sources.freeRenderCount > 0) {
            this.sources.freeRenderCount -= 1;
          }
        } else if (kind === "freePerspective") {
          if (this.sources.freePerspectiveCount > 0) {
            this.sources.freePerspectiveCount -= 1;
          }
        } else if (kind === "qwenFreePerspective") {
          if (this.sources.qwenFreePerspectiveCount > 0) {
            this.sources.qwenFreePerspectiveCount -= 1;
          }
        } else if (isGameUiSplitQuotaKind(kind)) {
          const key = GAME_UI_SPLIT_POOL_COUNT_KEYS[kind];
          if (this.sources[key] > 0) {
            this.sources[key] -= 1;
          }
        }
      }
    },

    optimisticallyConsumeActivityRight(moduleId: unknown, clientId: string) {
      if (!clientId) return false;
      if (this.pendingActivityRightDeductions?.has(clientId)) return false;

      const moduleNum = Number(moduleId);
      if (!Number.isFinite(moduleNum)) return false;

      const deductedAt = Date.now();

      if (moduleNum === ModuleId.Render) {
        if (this.sources.freeRenderCount > 0) {
          this.sources.freeRenderCount -= 1;
          this.pendingActivityRightDeductions.set(clientId, { kind: "render", deductedAt });
          return true;
        }
      }

      if (moduleNum === ModuleId.FreePerspective) {
        if (this.sources.freePerspectiveCount > 0) {
          this.sources.freePerspectiveCount -= 1;
          this.pendingActivityRightDeductions.set(clientId, { kind: "freePerspective", deductedAt });
          return true;
        }
      }
      // Qwen 自由视角：一键成稿(60) 与自由画布(61) 共享额度
      //@ts-ignore
      if ([ModuleId.QwenFreePerspective, ModuleId.CanvasQwenFreePerspective].includes(moduleNum)) {
        if (this.sources.qwenFreePerspectiveCount > 0) {
          this.sources.qwenFreePerspectiveCount -= 1;
          this.pendingActivityRightDeductions.set(clientId, { kind: "qwenFreePerspective", deductedAt });
          return true;
        }
      }

      return false;
    },

    /**
     * 全能拆分限免乐观扣减（TB-9531，池判定/消耗顺序见 ./gameUiSplitQuota.ts）
     * @returns true=已命中限免并扣 1 次（本次提交后端不扣积分，终态调用方须核销/回滚 pending）
     */
    optimisticallyConsumeGameUiSplitRight(clientId: string) {
      if (!clientId) return false;
      if (this.pendingActivityRightDeductions?.has(clientId)) return false;

      const kind = pickGameUiSplitQuotaPool(this.sources, this.isMemberUser);
      if (!kind) return false;

      const key = GAME_UI_SPLIT_POOL_COUNT_KEYS[kind];
      this.sources[key] -= 1;
      this.pendingActivityRightDeductions.set(clientId, { kind, deductedAt: Date.now() });
      return true;
    },

    finalizeActivityRightDeduction(clientId: string) {
      if (!clientId) return;
      this.pendingActivityRightDeductions?.delete(clientId);
    },

    rollbackActivityRightDeduction(clientId: string) {
      if (!clientId) return;
      const pending = this.pendingActivityRightDeductions?.get(clientId);
      if (!pending) return;

      this.pendingActivityRightDeductions.delete(clientId);

      if (this.activityRightsLastSyncedAt && this.activityRightsLastSyncedAt > pending.deductedAt) {
        this.queryActivityRights();
        return;
      }
      if (pending.kind === "render") {
        this.sources.freeRenderCount = Math.max(0, Number(this.sources.freeRenderCount) + 1);
      } else if (pending.kind === "freePerspective") {
        this.sources.freePerspectiveCount = Math.max(0, Number(this.sources.freePerspectiveCount) + 1);
      } else if (pending.kind === "qwenFreePerspective") {
        this.sources.qwenFreePerspectiveCount = Math.max(0, Number(this.sources.qwenFreePerspectiveCount) + 1);
      } else if (isGameUiSplitQuotaKind(pending.kind)) {
        // 退回扣减时的池子（trial/total/daily 各自独立），防跨日把永久池还到每日池虚增
        const key = GAME_UI_SPLIT_POOL_COUNT_KEYS[pending.kind];
        this.sources[key] = Math.max(0, Number(this.sources[key]) + 1);
      }
    },

    resetUserRights() {
      this.sources = { ...initSources };
      this.packages = [];
    },
    /**
     *  根据会员的时间类型和会员类型，返回对应的中文描述
     * @param param0
     * @returns
     */
    getMemberTimeLabel(timeMode: keyof typeof packageTimeEnum, memberType: keyof typeof vipPackage) {
      const memberTimeLabel = packageTimeEnum[timeMode];
      const memberTypeLabel = vipPackage[memberType].label;
      return `${memberTimeLabel}${["subscribe", "halfSubscribe"].includes(timeMode) ? "-" : ""}${memberTypeLabel}`;
    },

    usePayModal({ powerConsumption = 0, freeToast = true, powerPackage = false } = {}): boolean {
      // 判断当前用户所使用功能是否需要弹出购买vip、算力包弹出
      if (!this.showVip) {
        return false;
      }

      let flag = false;
      const payStore = usePayStore();

      if (this.highestPackage === "free") {
        const teamEditionStore = useTeamEditionStore();
        const isTeamContext = teamEditionStore.isTeamEdition || this.isGroupLogin;

        if (isTeamContext) {
          freeToast && message.warn("请联系管理员续费团队版功能");

          setTimeout(() => {
            const groupStore = useGroupStore();
            if (!groupStore.isSuperAdmin) {
              return;
            }
            payStore.useProductModal("groupMember");
          }, 1000);

          flag = true;
          return flag;
        }

        // 免费用户弹出vip开通弹窗
        freeToast && message.warn("该功能需升级为会员才能使用");

        setTimeout(() => {
          payStore.useProductModal("member");
        }, 1000);

        flag = true;
      } else if (this.powerNumber === 0 || this.powerNumber - powerConsumption < 0 || powerPackage) {
        // 付费会员用户可用算力为0时，弹出算力加量包弹窗
        // 团队版账号这一期不弹算力充值弹窗
        if (!this.isGroupLogin) {
          payStore.useProductModal("power");
          flag = true;
        }
      }

      return flag;
    },
    usePowerPackageModal() {
      const artworkStore = useArtworkStore();
      const groupStore = useGroupStore();
      const { isUnnormal } = toRefs(groupStore);
      if (isUnnormal.value) {
        message.warn("当前团队暂未下发权益，请联系管理员下发权益");
        return true;
      }

      if (artworkStore.channel === "expedite" && this.powerNumber === 0) {
        const teamEditionStore = useTeamEditionStore();

        const groupStore = useGroupStore();

        if (teamEditionStore.isTeamEdition && !groupStore.isSuperAdmin) {
          message.warn("您的团队加速算力已用完，请联系管理员购买");
          return true;
        }

        message.warn("您的会员加速算力已用完");

        const payStore = usePayStore();
        setTimeout(() => {
          payStore.useProductModal("power");
        }, 1000);
        return true;
      }
      return false;
    },

    async setCustom({ key, value }: { key: string; value: any }) {
      await setUserCustomStorage({
        key,
        value
      });
    },
    async getCustom(key: string) {
      const { data } = await getUserCustomStorage(key);
      return data;
    },
    /**
     * 判断是否需要显示新手问卷弹窗
     * 1. deviceEnv不为h5
     * 2. 用户userCollectModalKey 为空
     */
    async isShowNewUserInfoCollectModal() {
      const deviceEnv = getURLSearchParams("device_env");
      const hasCollect = await this.getCustom("userCollectModalKey");
      console.log("deviceEnv", deviceEnv);
      console.log("hasCollect", hasCollect);
      return deviceEnv !== "h5" && !hasCollect;
    },

    /**
     * 获取功能升级已读状态
     * @description 游客模式下不请求接口，使用默认空对象，upgrade 角标正常显示
     */
    async fetchFeatureUpgradeReadStatus() {
      // 游客模式不请求，使用默认值
      if (!this.isLogin) {
        this.featureUpgradeRead.data = {};
        this.featureUpgradeRead.isLoaded = true;
        return;
      }

      // 如果已经加载过，不再重复请求
      if (this.featureUpgradeRead.isLoaded) return;

      if (featureUpgradeReadFetchInFlight) {
        await featureUpgradeReadFetchInFlight;
        return;
      }

      const store = this;
      featureUpgradeReadFetchInFlight = (async () => {
        try {
          const res = await getUserCustomStorage("feature_upgrade_read");
          if (res.data) {
            const serverData = JSON.parse(res.data);
            store.featureUpgradeRead.data = { ...store.featureUpgradeRead.data, ...serverData };
          }
          store.featureUpgradeRead.isLoaded = true;
        } catch (error) {
          console.error("获取功能升级已读状态失败:", error);
          if (Object.keys(store.featureUpgradeRead.data).length > 0) {
            store.featureUpgradeRead.isLoaded = true;
          } else {
            store.featureUpgradeRead.data = {};
            store.featureUpgradeRead.isLoaded = true;
          }
        }
      })();

      try {
        await featureUpgradeReadFetchInFlight;
      } finally {
        featureUpgradeReadFetchInFlight = null;
      }
    },

    /**
     * 标记功能升级为已读
     * @param featureKey 功能标识，如 "render"
     * @param version 版本号，如 "1.2"
     * @description 游客模式下不调用此方法
     */
    async markFeatureUpgradeAsRead(featureKey: string, version: string) {
      // 游客模式不处理
      if (!this.isLogin) return;

      try {
        const key = `${featureKey}V${version.replace(/\./g, "")}`;

        // 更新本地状态
        this.featureUpgradeRead.data[key] = true;

        // 保存到服务端
        await setUserCustomStorage({
          key: "feature_upgrade_read",
          value: JSON.stringify(this.featureUpgradeRead.data)
        });
      } catch (error) {
        console.error("标记功能升级已读失败:", error);
      }
    },

    /**
     * 重置功能升级已读状态（用于登录后刷新）
     */
    resetFeatureUpgradeReadStatus() {
      this.featureUpgradeRead.data = {};
      this.featureUpgradeRead.isLoaded = false;
      featureUpgradeReadFetchInFlight = null;
    },

    /**
     * 更新水印/协议状态（统一入口）
     * v2 变更：替代旧的 fetchWatermarkStatus / agreeWatermarkProtocol / setWatermarkPreference
     *
     * @param targetStatus 目标状态值 (0-5)，直接传给后端 updateRealizeResult/status 接口
     */
    async updateWatermarkSetting(targetStatus: WatermarkStatusEnum) {
      const { data } = await updateWatermarkStatus(targetStatus);
      // 后端返回最新的 realizeResult，直接更新到 userInfo（响应式驱动所有UI刷新）
      if (data?.realizeResult !== undefined) {
        this.userInfo.realizeResult = data.realizeResult;
      } else {
        // 兜底：如果后端没返回 realizeResult，刷新整个 userInfo
        await this.queryUserInfo();
      }
    },

    /**
     * 加载用户水印弹窗首次观看状态
     * 从 setUserCustomStorage 加载，用户维度存储
     *
     * @description 需在用户登录后调用，通常在 queryUserInfo 之后
     */
    async loadWatermarkFirstSeen() {
      // 未登录状态下使用默认值
      if (!this.isLogin) {
        this.watermarkFirstSeen = { export: false, settings: false, isLoaded: true };
        return;
      }

      try {
        const res = await this.getCustom("watermark_modal_first_seen");
        const data = res ? JSON.parse(res) : null;

        this.watermarkFirstSeen = {
          export: data?.export ?? false,
          settings: data?.settings ?? false,
          isLoaded: true
        };
      } catch (error) {
        // 失败时使用默认值
        this.watermarkFirstSeen = { export: false, settings: false, isLoaded: true };
      }
    },

    /**
     * 标记已看过水印弹窗
     *
     * @param scene 场景：'export' 导出场景 | 'settings' 设置页场景
     */
    async markWatermarkModalSeen(scene: "export" | "settings") {
      if (!this.isLogin) return;

      // 更新内存状态
      this.watermarkFirstSeen[scene] = true;

      try {
        // 保存到用户自定义存储
        await this.setCustom({
          key: "watermark_modal_first_seen",
          value: JSON.stringify({
            export: this.watermarkFirstSeen.export,
            settings: this.watermarkFirstSeen.settings
          })
        });
      } catch (error) {
        console.error("标记水印弹窗状态失败:", error);
      }
    },

    /**
     * 加载VIP升级首次下载状态
     *
     * @description 需在用户登录后调用，通常在 queryUserInfo 之后
     */
    async loadVipUpgradeDownloadStatus() {
      // 未登录状态下使用默认值
      if (!this.isLogin) {
        this.vipUpgradeDownloadStatus = { seen: false, lastMemberStatus: null, isLoaded: true };
        return;
      }

      try {
        const res = await this.getCustom("vip_upgrade_first_download");
        const data = res ? JSON.parse(res) : null;

        this.vipUpgradeDownloadStatus = {
          seen: data?.seen ?? false,
          lastMemberStatus: data?.lastMemberStatus ?? null,
          isLoaded: true
        };
      } catch (error) {
        // 失败时使用默认值
        this.vipUpgradeDownloadStatus = { seen: false, lastMemberStatus: null, isLoaded: true };
      }
    },

    /**
     * 标记已看过VIP升级首次下载弹窗
     *
     * @description 在弹窗显示时立即调用，确保状态持久化
     */
    async markVipUpgradeDownloadSeen() {
      if (!this.isLogin) return;

      // 更新内存状态
      this.vipUpgradeDownloadStatus.seen = true;

      try {
        // 保存到用户自定义存储
        await this.setCustom({
          key: "vip_upgrade_first_download",
          value: JSON.stringify({
            seen: this.vipUpgradeDownloadStatus.seen,
            lastMemberStatus: this.vipUpgradeDownloadStatus.lastMemberStatus
          })
        });
      } catch (error) {
        console.error("标记VIP升级下载弹窗状态失败:", error);
      }
    },

    /**
     * 更新会员状态快照（用于检测升级）
     *
     * @param isMember 当前是否为会员
     */
    async updateLastMemberStatus(isMember: boolean) {
      if (!this.isLogin) return;

      // 更新内存状态
      this.vipUpgradeDownloadStatus.lastMemberStatus = isMember;

      try {
        // 保存到用户自定义存储
        await this.setCustom({
          key: "vip_upgrade_first_download",
          value: JSON.stringify({
            seen: this.vipUpgradeDownloadStatus.seen,
            lastMemberStatus: this.vipUpgradeDownloadStatus.lastMemberStatus
          })
        });
      } catch (error) {
        console.error("更新会员状态快照失败:", error);
      }
    },

    /**
     * 重置水印弹窗首次观看状态
     * @description 用户退出登录时调用，仅重置内存状态，不删除存储
     */
    resetWatermarkFirstSeen() {
      this.watermarkFirstSeen = {
        export: false,
        settings: false,
        isLoaded: false
      };
    }
  },
  persist: {
    key: "pinoUserStore",
    // featureUpgradeRead 不持久化：与账号绑定数据以服务端 customStorage 为准，避免换号后从 localStorage 恢复上一账号已读状态
    paths: ["userInfo", "sources", "packages"],
    storage: window.localStorage,
    afterRestore: (ctx) => {
      const token = ctx.store.userInfo?.token;
      if (token) {
        globalStore.set("token", token);
        logToken("Pinia persist 恢复 token", { tokenPrefix: token.substring(0, 8) });
      }
    }
  }
});

export { getConsumePower, getConsumePowerLabel };
